import { Delete, EmailOutlined, PhoneOutlined } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { ReactComponent as ExportIconSvg } from 'assets/svg/ExportSharableLink.svg';
import BuyerPoolLoader from 'components/Loading/BuyerPoolLoader';
import DeleteSavedRequest from 'components/client-pool/DeleteRequestModal';
import MockedSavedRequests from 'components/client-pool/MockedSavedRequests';
import SavedLeadDetailsModal from 'components/client-pool/SavedLeadDetailsModal';
import CustomBadge from 'components/CustomBadge';
import { PropertyPagination } from 'components/Pagination';
import { useDownloadCSVPropertyRequests, useSavedOrRemovePropertyRequest, useSavedPropertyRequests } from 'hooks/usePropertyRequests';
import { useState } from 'react';
import usePropertyRequestsStore from 'store/propertyRequestsStore';
import useUserStore from 'store/useUserStore';
import { PropertyLead } from 'types/PropertyLead';
import { formatDate } from 'util/formatDate';
import NoItems from './NoItems';

const SavedRequests = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { savedRequestsPage, setSavedRequestsPage, setSelectedLead, selectedLead } = usePropertyRequestsStore();
    const { data, isLoading } = useSavedPropertyRequests();
    const [deleteRequestModalVisible, setDeleteRequestModalVisible] = useState(false);
    const { mutate } = useSavedOrRemovePropertyRequest();
    const { mutate: downloadCSV, isPending } = useDownloadCSVPropertyRequests();
    const { getUserPlan } = useUserStore();
    const userPlan = getUserPlan();

    const totalPages = Math.ceil(data?.data?.data?.totalCount / 10);

    if (userPlan.title === 'Free') {
        return (
            <div className="pb-10 pt-6 relative">
                <div className="absolute inset-0 z-0">
                    <MockedSavedRequests />
                </div>
                <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-sm" />
                <div className="relative z-20 flex items-center justify-center min-h-[60vh]">
                    <NoItems title="Upgrade Your Plan" description="Upgrade your plan to save and inspect buyer requests. Get access to advanced features!" />
                </div>
            </div>
        );
    }

    if (data?.data?.data?.totalCount === 0) return <NoItems />;

    if (isLoading) return <BuyerPoolLoader />;

    return (
        <div className="pb-10 pt-6">
            <div className="pt-4  bg-white min-h-screen border border-my-gray/70 rounded-lg px-6">
                <div className="flex flex-col gap-6 w-full font-poppins  pt-6  rounded-lg ">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-2">
                            <h6 className="font-semibold text-my-black/80 text-base ">Saved Requests</h6>
                            <span className="h-fit rounded-sm text-white px-2 py-0.5   bg-my-green">{data?.data?.data?.totalCount}</span>
                        </div>
                        <button
                            className="flex gap-3 sm:w-40  md:w-64 lg:w-auto items-center justify-center border-2 border-blue-500 rounded h-min py-2 px-5"
                            onClick={() => downloadCSV()}
                            disabled={isPending}
                        >
                            {isPending ? <CircularProgress size={20} /> : <ExportIconSvg />}
                            <span>Export</span>
                        </button>
                    </div>
                    <div className="w-full border border-my-gray/70  mb-6 rounded-t-2xl relative">
                        <div className="grid uppercase default-xl:grid-cols-7 default-sm:grid-cols-5 grid-cols-3 gap-10 items-center text-my-black/60 border border-my-gray/10 border-opacity-40 py-[15px] px-6  bg-my-gray/15  rounded-t-2xl font-medium default-sm:text-base text-[12px]">
                            <span className="default-xl:block hidden"> Request Date</span>
                            <span className="col-span-2 default-xl:col-span-1">Title</span>
                            <span className="default-sm:block hidden">Location</span>
                            <span className="col-span-2 default-xl:block hidden">Contact Information</span>
                            <span className="default-sm:block hidden">Status</span>
                            <span>Actions</span>
                        </div>
                        <div className="flex flex-col  text-opacity-80 ">
                            {data?.data?.data?.leads?.length > 0 &&
                                data?.data?.data?.leads?.map((listing: PropertyLead, index: number) => (
                                    <div key={listing.id} className={`p-4 rounded-lg ${index % 2 === 0 ? 'bg-white/60' : 'bg-my-gray/15'}`}>
                                        <div className="grid default-xl:grid-cols-7 default-sm:grid-cols-5 grid-cols-3 text-my-black/80 px-4 font-medium default-sm:text-base text-[12px] gap-10 items-center">
                                            <span className="default-xl:block hidden">{formatDate(listing.createdAt)}</span>
                                            <span className="col-span-2 default-xl:col-span-1">{listing.title}</span>
                                            <span className="default-sm:block hidden">
                                                {listing.definedArea ? listing.definedArea : listing.cities.map((city) => city.city).join(', ')}
                                            </span>
                                            <div className=" flex-col gap-4 col-span-2 default-xl:flex hidden">
                                                <div className="flex gap-4">
                                                    <EmailOutlined className="text-my-black/50" />
                                                    <span>{listing.contactInfo[0].email}</span>
                                                </div>
                                                <div className="flex gap-4">
                                                    <PhoneOutlined className="text-my-black/50" />
                                                    <span>{listing.contactInfo[0].phone}</span>
                                                </div>
                                            </div>
                                            <CustomBadge status={listing.status} isOpen={false} onToggle={() => {}} />
                                            <div className="items-center justify-between default-sm:flex hidden">
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setSelectedLead(listing);
                                                        setIsOpen(true);
                                                    }}
                                                >
                                                    Details
                                                </Button>
                                                <div className="cursor-pointer default-xl:block hidden">
                                                    <Delete
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedLead(listing);
                                                            setDeleteRequestModalVisible(true);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                        {data?.data?.data?.totalCount > 1 && (
                            <div className="mx-auto w-fit pt-3 mb-6 ">
                                <PropertyPagination handlePageChange={setSavedRequestsPage} totalPageCount={totalPages} currentPage={savedRequestsPage} />
                            </div>
                        )}
                    </div>
                </div>
                <SavedLeadDetailsModal isOpen={isOpen} setIsOpen={setIsOpen} />
                <DeleteSavedRequest
                    visible={deleteRequestModalVisible}
                    onClose={() => setDeleteRequestModalVisible(false)}
                    onRemove={() => mutate({ propertyLeadId: selectedLead?.id!, action: 'remove' })}
                />
            </div>
        </div>
    );
};

export default SavedRequests;
